import { toString } from 'lodash';
import { safeJsonParse } from '../../utils/json';
import { ComparableValue } from './api/ComparableValue';
import type { DynamicFieldFilterContext } from './api/DynamicFieldFilterContext';
import type { Field } from './api/Field';
import type { Filter } from './api/Filter';
import { FilterBuilderFactory } from './api/FilterBuilderFactory';
import type { FilterEvaluator } from './api/FilterEvaluator';
import { DataType } from './api/Op';

interface ConditionEvaluator {
  evaluate: (filter: Filter) => boolean;
}

export class ConditionEvaluatorImpl implements ConditionEvaluator {
  evaluate(_filter: Filter | null): boolean {
    if (_filter === null) {
      return false;
    }

    class ImplementedDynamicFilterContext implements DynamicFieldFilterContext<object> {
      createField(_filter: Filter): Field<object> {
        return new ConditionEvaluatorImpl().createFieldWithFilter(_filter);
      }
    }

    const filterEvaluator: FilterEvaluator<object> = FilterBuilderFactory.INSTANCE.build<object>(
      new ImplementedDynamicFilterContext(),
      _filter,
    );

    return filterEvaluator.evaluate(_filter);
  }

  createFieldWithFilter(filter: Filter): Field<object> {
    const fieldOrTemplate = filter.getField();
    const expectedDataType = filter.getOp()?.getExpectedDataType();

    if (expectedDataType && expectedDataType !== DataType.ANY) {
      return this.createFieldWithDataType(fieldOrTemplate, expectedDataType);
    }

    const dataType = this.identifyDataTypeWithFilter(filter);
    return this.createFieldWithDataType(fieldOrTemplate, dataType);
  }

  private createFieldWithDataType(field: unknown, dataType: DataType): Field<object> {
    return {
      getValues: () => {
        // used in missing and exists
        if (field === null || field === undefined) {
          return null;
        }
        return [new ComparableValue(this.toComparableValue(field, dataType), dataType)];
      },
      toValues: (values?: object[]) => {
        if (!values || values.length === 0) {
          return [];
        }
        const resolvedValues: ComparableValue<unknown>[] = [];
        for (const value of values) {
          resolvedValues.push(
            new ComparableValue(this.toComparableValue(value, dataType), dataType),
          );
        }
        return resolvedValues;
      },
    };
  }

  private toComparableValue(
    value: string | number | boolean | object | null | undefined,
    dataType: DataType,
  ) {
    if (value === null) {
      return null;
    }
    switch (dataType) {
      case DataType.ANY:
      case DataType.OBJECT:
        return value;
      case DataType.NUMBER:
        if (typeof value === 'string' && value.trim() === '') {
          return NaN;
        }
        return typeof value === 'number' ? value : Number(value);
      case DataType.STRING:
        return toString(value);
      case DataType.BOOLEAN:
        // eslint-disable-next-line no-nested-ternary -- remove
        return typeof value === 'boolean'
          ? value
          : typeof value === 'string'
            ? safeJsonParse(`${value}`, value as unknown as Record<string, unknown>)
            : value;
    }
  }

  private identifyDataTypeWithFilter(filter: Filter): DataType {
    const objectsToCheck = this.collectAllOperands(filter);
    const dataType: DataType | null = this.identityDataTypeFromTemplatedStrings(objectsToCheck);
    return dataType === null ? DataType.STRING : dataType;
  }

  private identityDataTypeFromTemplatedStrings(objectsToCheck: unknown[]): DataType | null {
    for (const object of objectsToCheck) {
      if (typeof object === 'string') {
        return this.identifyDataType(object);
      }
    }
    return null;
  }

  private collectAllOperands(filter: Filter): unknown[] {
    const objectsToCheck: unknown[] = [];
    if (filter.getField()) {
      objectsToCheck.push(filter.getField());
    }
    const values = filter.getValues();
    const nullsafeList = values?.filter((value) => value !== null) ?? [];
    objectsToCheck.push(...nullsafeList);
    return objectsToCheck;
  }

  private identifyDataType(value: unknown): DataType | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim().toLowerCase();

      if (trimmedValue === 'true' || trimmedValue === 'false') {
        return DataType.BOOLEAN;
      }

      if (!Number.isNaN(Number(trimmedValue)) && trimmedValue !== '') {
        return DataType.NUMBER;
      }

      if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
        const arrayContent = trimmedValue.slice(1, -1).trim();
        return arrayContent ? this.identifyDataType(arrayContent.split(',')[0]) : null;
      }

      if (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) {
        return DataType.OBJECT;
      }

      return DataType.STRING;
    }

    if (typeof value === 'number') {
      return DataType.NUMBER;
    }
    if (typeof value === 'boolean') {
      return DataType.BOOLEAN;
    }
    if (Array.isArray(value)) {
      return value.length === 0 ? null : this.identifyDataType(value[0]);
    }
    if (typeof value === 'object') {
      return DataType.OBJECT;
    }

    return DataType.STRING;
  }
}
